import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ForecastFormPanel } from './ForecastFormPanel';
import type { DirectionFilter, StatusFilter } from './dashboard-model';
import type { ForecastFormController } from './useForecastForm';

type FilterButtonProps = {
  label: string;
  active: boolean;
  onSelect: () => void;
};

function FilterButton({ label, active, onSelect }: FilterButtonProps) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? 'default' : 'outline'}
      aria-pressed={active}
      onClick={onSelect}
    >
      {label}
    </Button>
  );
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="card-detail">{label}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

type DashboardFiltersCardProps = {
  forecastForm: ForecastFormController | null;
  directionFilter: DirectionFilter;
  onDirectionChange: (value: DirectionFilter) => void;
  statusFilter: StatusFilter;
  onStatusChange: (value: StatusFilter) => void;
  categoryFilter: string;
  availableCategories: string[];
  onCategoryChange: (value: string) => void;
  projectLabelFilter: string;
  availableProjectLabels: string[];
  onProjectLabelChange: (value: string) => void;
};

export function DashboardFiltersCard(props: DashboardFiltersCardProps) {
  const { t } = useTranslation('finance');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('dashboard.filtersTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {props.forecastForm && <ForecastFormPanel form={props.forecastForm} />}

        <FilterGroup label={t('dashboard.directionFilterLabel')}>
          {(['all', 'inflow', 'outflow'] as const).map((value) => (
            <FilterButton
              key={value}
              label={t(`dashboard.directionFilters.${value}`)}
              active={props.directionFilter === value}
              onSelect={() => props.onDirectionChange(value)}
            />
          ))}
        </FilterGroup>

        <FilterGroup label={t('dashboard.statusFilterLabel')}>
          {(['all', 'Actual', 'Forecast'] as const).map((value) => (
            <FilterButton
              key={value}
              label={t(`dashboard.statusFilters.${value}`)}
              active={props.statusFilter === value}
              onSelect={() => props.onStatusChange(value)}
            />
          ))}
        </FilterGroup>

        <FilterGroup label={t('dashboard.categoryFilterLabel')}>
          <FilterButton
            label={t('dashboard.categoryFilters.all')}
            active={props.categoryFilter === 'all'}
            onSelect={() => props.onCategoryChange('all')}
          />
          {props.availableCategories.map((category) => (
            <FilterButton
              key={category}
              label={t('dashboard.categoryFilters.value', { category })}
              active={props.categoryFilter === category}
              onSelect={() => props.onCategoryChange(category)}
            />
          ))}
        </FilterGroup>

        <FilterGroup label={t('dashboard.projectLabelFilterLabel')}>
          <FilterButton
            label={t('dashboard.projectLabelFilters.all')}
            active={props.projectLabelFilter === 'all'}
            onSelect={() => props.onProjectLabelChange('all')}
          />
          {props.availableProjectLabels.map((projectLabel) => (
            <FilterButton
              key={projectLabel}
              label={t('dashboard.projectLabelFilters.value', { projectLabel })}
              active={props.projectLabelFilter === projectLabel}
              onSelect={() => props.onProjectLabelChange(projectLabel)}
            />
          ))}
        </FilterGroup>
      </CardContent>
    </Card>
  );
}
